/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsEntityInputType = require('./PictProvider-InputType-MaterialsEntitySelector.js');
const ScopedEntityData = require('./_ScopedEntityData.js');
const libLabRouting = require('./_LabRouting.js');

class MaterialsTestSelectorInputType extends MaterialsEntityInputType
{
	constructor(pFable, pOptions, pServiceHash)
	{
		if (!pOptions)
		{
			pOptions = { };
		}
		pOptions.Entity = 'Test'; //TODO: use default options for this?
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict & { TransactionTracking: any }} */
		this.pict;
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Hash';
		}
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		// sample is in charge of the family for tracking
		if (pInput.PictForm.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.TestTrackingGUID, 'TEST');
			return tmpResult;
		}
		let tmpTrackingGUID = this._getTrackingGUID(pInput, null, 'TEST');
		if (!tmpTrackingGUID)
		{
			tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, null, tmpTrackingGUID, 'TEST');
			pInput.PictForm.TestTrackingGUID = tmpTrackingGUID;
		}
		return tmpResult;
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Hash';
		}
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		if (pInput.PictForm.TestTrackingGUID && pInput.PictForm.TestTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.TestTrackingGUID, 'TEST');
			return tmpResult;
		}
		let tmpTrackingGUID = this._getTrackingGUID(pInput, pRowIndex, 'TEST');
		if (!tmpTrackingGUID)
		{
			tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID, 'TEST');
			pInput.PictForm.TestTrackingGUID = true; // different rows will have different values so avoid confusion
		}
		return tmpResult;
	}

	/**
	 * @param {Object} pInput
	 *
	 * @return {string}
	 */
	_getDefaultRecordIdentity(pInput)
	{
		return 'Hash';
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string} [pRowIndex]
	 */
	_getDefaultRecord(pView, pInput, pRowIndex)
	{
		let tmpRecord = super._getDefaultRecord(pView, pInput, pRowIndex);
		if (!tmpRecord)
		{
			//FIXME: need to get some kind of record uniqueness injected here
			tmpRecord = { GUIDTest: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-T-${this._getSalt(pView, pInput, pRowIndex)}` };
		}
		return tmpRecord;
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex
	 * @param {string} pEvent
	 * @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'TestResultChanged')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex, 'TEST'))
			{
				return;
			}
			const tmpTestResult = tmpEventPayload;

			if (!this._isMultiple(pInput))
			{
				const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				const tmpTestInstanceAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instance`;
				const tmpTestInstance = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress);
				if (tmpTestInstance && typeof tmpTestInstance === 'object' && !Array.isArray(tmpTestInstance) && tmpTestInstance.GUIDTestInstance)
				{
					tmpTestInstance.TestResult = tmpTestResult;
					this._stageDeferredTestResult(tmpTestInstance, pInput);
				}
				return;
			}
			for (const tmpTriple of this._getTestInstances(pView, pInput, pRowIndex))
			{
				const tmpTestInstance = tmpTriple && tmpTriple.TestInstance;
				if (tmpTestInstance && typeof tmpTestInstance === 'object' && !Array.isArray(tmpTestInstance) && tmpTestInstance.GUIDTestInstance)
				{
					tmpTestInstance.TestResult = tmpTestResult;
					this._stageDeferredTestResult(tmpTestInstance, pInput);
				}
			}
			return;
		}

		if (tmpEventType === 'GetTestHash')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex, 'TEST'))
			{
				return;
			}
			const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpTest = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestAddress);
			const tmpTestRecord = Array.isArray(tmpTest) ? tmpTest[0] : tmpTest;
			const tmpTestHash = tmpTestRecord ? tmpTestRecord.Hash : null;
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload, tmpTestHash);
		}

		if (tmpEventType === 'SampleChanged')
		{
			if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
			{
				return;
			}
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpEventPayload);
			if (!tmpSampleRecord || typeof tmpSampleRecord !== 'object' || Array.isArray(tmpSampleRecord))
			{
				return;
			}
			if (!this._isMultiple(pInput))
			{
				const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				const tmpTestInstanceSampleJoinAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_SampleJoin`;
				const tmpTestInstanceSampleJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceSampleJoinAddress);
				if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object' && !Array.isArray(tmpTestInstanceSampleJoin) && tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin)
				{
					tmpTestInstanceSampleJoin.IDSample = tmpSampleRecord.IDSample;
					if (!tmpTestInstanceSampleJoin.IDSample)
					{
						tmpTestInstanceSampleJoin.GUIDSample = tmpSampleRecord.GUIDSample;
						delete tmpTestInstanceSampleJoin.IDSample;
					}
					else
					{
						delete tmpTestInstanceSampleJoin.GUIDSample;
					}
				}
				return;
			}
			if (tmpSampleRecord.IDSample)
			{
				this._loadSampleTests(pView, pInput, pRowIndex, pTransactionGUID, tmpSampleRecord);
			}
			for (const tmpTriple of this._getTestInstances(pView, pInput, pRowIndex))
			{
				const tmpTestInstanceSampleJoin = tmpTriple && tmpTriple.TestInstanceSampleJoin;
				if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object' && !Array.isArray(tmpTestInstanceSampleJoin) && tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin)
				{
					tmpTestInstanceSampleJoin.IDSample = tmpSampleRecord.IDSample;
					if (!tmpTestInstanceSampleJoin.IDSample)
					{
						tmpTestInstanceSampleJoin.GUIDSample = tmpSampleRecord.GUIDSample;
						delete tmpTestInstanceSampleJoin.IDSample;
					}
					else
					{
						delete tmpTestInstanceSampleJoin.GUIDSample;
					}
				}
			}
			return;
		}
		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			if (!this._isMultiple(pInput))
			{
				const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
				const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpTestAddress}_DocumentPolyJoin`;
				const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
				if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
				{
					if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
					{
						tmpTarget.DocumentPolyJoin = { };
					}
					tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
				}
				return;
			}
			for (const tmpTriple of this._getTestInstances(pView, pInput, pRowIndex))
			{
				const tmpDocumentPolyJoin = tmpTriple && tmpTriple.DocumentPolyJoin;
				if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
				{
					if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
					{
						tmpTarget.DocumentPolyJoin = { };
					}
					tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
				}
			}
		}
	}

	/**
	 * Whether this input is configured for multi-test selection. The non-Multiple (single-select) path keeps the
	 * legacy singular data shapes (one TestInstance object at ${addr}_Instance, un-suffixed GUIDs); the Multiple
	 * path builds the keyed _Instances triple collection with per-test GUID suffixes.
	 *
	 * @param {Object} pInput - The input object.
	 *
	 * @return {boolean}
	 */
	_isMultiple(pInput)
	{
		return !!(pInput && pInput.PictForm && pInput.PictForm.Multiple);
	}

	/**
	 * Read the TestInstance triple collection (TestInstance / TestInstanceSampleJoin / DocumentPolyJoin per
	 * selected Test) for this input/row, always as an array (empty when unset or malformed).
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} [pRowIndex] - The row index. If present, this is a tabular input.
	 *
	 * @return {Array<{ TestInstance: Record<string, any>, TestInstanceSampleJoin: Record<string, any>, DocumentPolyJoin: (Record<string, any>|null) }>}
	 */
	_getTestInstances(pView, pInput, pRowIndex)
	{
		const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpTestInstancesAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instances`;
		const tmpTestInstances = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstancesAddress);
		return Array.isArray(tmpTestInstances) ? tmpTestInstances : [ ];
	}

	/**
	 * A de-selected Test takes its whole triple with it — the `TestInstance`, its
	 * `TestInstanceSampleJoin` and its `DocumentPolyJoin` — none of which the base sees, because the
	 * value list holds Test identities and the triples are keyed by `IDTest`.
	 *
	 * The retained `IDTest` set is resolved from the records the input STILL holds, since those are
	 * the only place the identity-to-`IDTest` mapping exists before `_refreshRecord` replaces them.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {Set<string>} pRetainedIdentities
	 * @return {Array<{ Entity: string, Record: Record<string, any> }>}
	 */
	_collectRemovedDependentRecords(pView, pInput, pRowIndex, pRetainedIdentities)
	{
		const tmpTriples = this._getTestInstances(pView, pInput, pRowIndex);
		if (tmpTriples.length < 1)
		{
			return [ ];
		}
		const tmpIdentity = this._getRecordIdentity(pInput);
		const tmpHeld = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pView, pInput, pRowIndex));
		const tmpRetainedIDTests = new Set();
		for (const tmpRecord of Array.isArray(tmpHeld) ? tmpHeld : [ tmpHeld ])
		{
			if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord))
			{
				continue;
			}
			if (pRetainedIdentities.has(String(tmpRecord[tmpIdentity])))
			{
				tmpRetainedIDTests.add(String(tmpRecord.IDTest));
			}
		}
		const tmpRemovals = [ ];
		for (const tmpTriple of tmpTriples)
		{
			const tmpTestInstance = tmpTriple && tmpTriple.TestInstance;
			if (!tmpTestInstance || typeof tmpTestInstance !== 'object')
			{
				continue;
			}
			if (tmpRetainedIDTests.has(String(tmpTestInstance.IDTest)))
			{
				continue;
			}
			tmpRemovals.push(...ScopedEntityData.collectTestInstanceUnit(tmpTriple));
		}
		return tmpRemovals;
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * Build (or reuse) the TestInstance / TestInstanceSampleJoin / DocumentPolyJoin triple for a single selected
	 * Test record. When a matching triple already exists in pCurrentInstances (same IDTest) it is reused so any
	 * server-assigned ids and downstream edits are preserved; otherwise a fresh triple is synthesized with a
	 * per-test-unique GUID (suffixed with the test's IDTest, the way the Lab selector suffixes -${IDLab}).
	 *
	 * @param {Record<string, any>} pRecord - The selected Test record.
	 * @param {Array<{ TestInstance: Record<string, any>, TestInstanceSampleJoin: Record<string, any>, DocumentPolyJoin: (Record<string, any>|null) }>} pCurrentInstances - Existing triples to diff against (matches are spliced out).
	 * @param {Record<string, any>|null} pSample - The bound Sample record (or null).
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index. If present, this is a tabular input.
	 *
	 * @return {{ TestInstance: Record<string, any>, TestInstanceSampleJoin: Record<string, any>, DocumentPolyJoin: (Record<string, any>|null) }}
	 */
	_buildTestInstanceTriple(pRecord, pCurrentInstances, pSample, pView, pInput, pRowIndex)
	{
		const tmpSalt = this._getSalt(pView, pInput, pRowIndex);
		// Per-test GUID suffix so multiple instances built off one salt don't collide.
		const tmpGUIDSuffix = (pRecord && pRecord.IDTest != null) ? `-${pRecord.IDTest}` : '';
		const tmpTestHash = (pRecord && pRecord.Hash) || '';
		const tmpDocument = this.pict.AppData.DocumentData.ReportData;

		const tmpExistingIndex = Array.isArray(pCurrentInstances) ? pCurrentInstances.findIndex((pTriple) => pTriple && pTriple.TestInstance && pTriple.TestInstance.IDTest == pRecord.IDTest) : -1;
		let tmpTestInstance;
		let tmpTestInstanceSampleJoin;
		let tmpDocumentPolyJoin;
		let tmpExistingTriple = null;
		if (tmpExistingIndex >= 0)
		{
			tmpExistingTriple = pCurrentInstances[tmpExistingIndex];
			pCurrentInstances.splice(tmpExistingIndex, 1);
			tmpTestInstance = tmpExistingTriple.TestInstance;
			tmpTestInstanceSampleJoin = tmpExistingTriple.TestInstanceSampleJoin;
			tmpDocumentPolyJoin = tmpExistingTriple.DocumentPolyJoin;
		}

		if (!tmpTestInstance)
		{
			tmpTestInstance = JSON.parse(JSON.stringify(pInput.PictForm.DefaultTestInstanceRecord ||
				{
					GUIDTestInstance: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TI-${tmpSalt}`,
					IDTest: pRecord.IDTest,
				}));
			if (tmpTestInstance && typeof tmpTestInstance === 'object')
			{
				this._translateRecordTemplateFields(tmpTestInstance);
			}
			if (!tmpTestInstance.GUIDTestInstance)
			{
				tmpTestInstance.GUIDTestInstance = `${this.pict.parseTemplate('{~D:AppData.DocumentData.ReportData.GUIDDocument~}', this.pict)}-TI-${tmpSalt}`;
			}
			tmpTestInstance.GUIDTestInstance += tmpGUIDSuffix;
			tmpTestInstance.IDTest = pRecord.IDTest;
		}
		else
		{
			if (!tmpTestInstance.GUIDTestInstance)
			{
				tmpTestInstance.GUIDTestInstance = `${this.pict.parseTemplate('{~D:AppData.DocumentData.ReportData.GUIDDocument~}', this.pict)}-TI-${tmpSalt}${tmpGUIDSuffix}`;
			}
			tmpTestInstance.IDTest = pRecord.IDTest;
			this._translateRecordTemplateFields(tmpTestInstance);
		}
		if (!tmpTestInstance.Name)
		{
			tmpTestInstance.Name = `Sample Test for ${tmpTestHash}`;
		}
		if (!(tmpTestInstance.IDProject > 0) && tmpDocument && tmpDocument.IDProject > 0)
		{
			tmpTestInstance.IDProject = tmpDocument.IDProject;
		}
		if (pRecord && typeof pRecord === 'object')
		{
			if (pRecord.IDTest > 0)
			{
				tmpTestInstance.IDTest = pRecord.IDTest;
				delete tmpTestInstance.GUIDTest;
			}
			else if (pRecord.GUIDTest)
			{
				tmpTestInstance.GUIDTest = pRecord.GUIDTest;
			}
		}

		if (!tmpDocumentPolyJoin)
		{
			tmpDocumentPolyJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultDocumentPolyJoinRecord ||
				{
					GUIDDocumentPolyJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TI_DPJ-${tmpSalt}`,
					RemoteType: 'TestInstance',
					IDDocument: `{~D:AppData.DocumentData.ReportData.IDDocument~}`,
					GUIDDocument: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}`,
					GUIDTestInstance: tmpTestInstance.GUIDTestInstance,
					_Dest_GUIDTestInstance: 'IDRemote',
				}));
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object')
			{
				this._translateRecordTemplateFields(tmpDocumentPolyJoin);
			}
			if (!tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				tmpDocumentPolyJoin.GUIDDocumentPolyJoin = this.pict.getUUID();
			}
			else
			{
				tmpDocumentPolyJoin.GUIDDocumentPolyJoin += tmpGUIDSuffix;
			}
			tmpDocumentPolyJoin.GUIDTestInstance = tmpTestInstance.GUIDTestInstance;
		}
		if (tmpDocument)
		{
			if (tmpDocument.IDDocument > 0)
			{
				tmpDocumentPolyJoin.IDDocument = tmpDocument.IDDocument;
				delete tmpDocumentPolyJoin.GUIDDocument;
			}
			else
			{
				tmpDocumentPolyJoin.GUIDDocument = tmpDocument.GUIDDocument;
				delete tmpDocumentPolyJoin.IDDocument;
			}
		}

		if (!tmpTestInstanceSampleJoin)
		{
			tmpTestInstanceSampleJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultTestInstanceSampleJoinRecord ||
				{
					GUIDTestInstanceSampleJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TISJ-${tmpSalt}`,
				}));
			if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object')
			{
				this._translateRecordTemplateFields(tmpTestInstanceSampleJoin);
			}
			if (!tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin)
			{
				tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin = this.pict.getUUID();
			}
			else
			{
				tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin += tmpGUIDSuffix;
			}
		}
		if (tmpTestInstance && tmpTestInstance.GUIDTestInstance)
		{
			if (tmpTestInstance.IDTestInstance > 0)
			{
				tmpTestInstanceSampleJoin.IDTestInstance = tmpTestInstance.IDTestInstance;
				delete tmpTestInstanceSampleJoin.GUIDTestInstance;
			}
			else
			{
				tmpTestInstanceSampleJoin.GUIDTestInstance = tmpTestInstance.GUIDTestInstance;
			}
		}
		//FIXME: should these use GUIDs to connect the records for offline? probably, but not sure how that'll work e2e
		if (pSample && typeof pSample === 'object')
		{
			if (pSample.IDSample > 0)
			{
				tmpTestInstanceSampleJoin.IDSample = pSample.IDSample;
				delete tmpTestInstanceSampleJoin.GUIDSample;
			}
			else if (pSample.GUIDSample)
			{
				tmpTestInstanceSampleJoin.GUIDSample = pSample.GUIDSample;
			}
		}

		// Name is a display-only flat field for the tabular Test column; the wrapper is never persisted (the projection
		// writes TestInstance / TestInstanceSampleJoin / DocumentPolyJoin), so it needs no strip. A reused triple keeps
		// the row state other inputs park on it — the per-test lab selector's `_TestInstanceLabJoin`.
		return Object.assign({ }, tmpExistingTriple, { Name: (tmpTestInstance && tmpTestInstance.Name) || '', TestInstance: tmpTestInstance, TestInstanceSampleJoin: tmpTestInstanceSampleJoin, DocumentPolyJoin: tmpDocumentPolyJoin });
	}

	/**
	 * Diff the currently-selected Tests against the existing TestInstance triples, reusing matched triples (same
	 * IDTest), synthesizing new ones for newly-selected tests, and dropping deselected ones. Mirrors the Lab
	 * selector's _syncSampleLabJoins, but each entry carries the full TestInstance/TestInstanceSampleJoin/
	 * DocumentPolyJoin triple rather than a single join.
	 *
	 * @param {Array<Record<string, any>>} pRecords - The currently-selected Test records.
	 * @param {Array<{ TestInstance: Record<string, any>, TestInstanceSampleJoin: Record<string, any>, DocumentPolyJoin: (Record<string, any>|null) }>} pCurrentInstances - The existing triples to diff against.
	 * @param {Record<string, any>|null} pSample - The bound Sample record (or null).
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index. If present, this is a tabular input.
	 *
	 * @return {Array<{ TestInstance: Record<string, any>, TestInstanceSampleJoin: Record<string, any>, DocumentPolyJoin: (Record<string, any>|null) }>}
	 */
	_syncTestInstances(pRecords, pCurrentInstances, pSample, pView, pInput, pRowIndex)
	{
		if (!Array.isArray(pCurrentInstances))
		{
			pCurrentInstances = [];
		}
		const tmpInstances = [ ];
		for (const tmpRecord of pRecords || [])
		{
			if (!tmpRecord || typeof tmpRecord !== 'object')
			{
				continue;
			}
			tmpInstances.push(this._buildTestInstanceTriple(tmpRecord, pCurrentInstances, pSample, pView, pInput, pRowIndex));
		}
		return tmpInstances;
	}

	/**
	 * Soft-adopt the bound Sample's persisted tests from the DB on (re)open — mirrors the Lab/LineItem selectors'
	 * SampleChanged loaders (load persisted records by IDSample, set them as the current records, then setDataByInput
	 * so the rebuild reconciles by reusing them). The doc's saved FormData triples can be stale client placeholders
	 * that were never persisted — e.g. the materials concierge creates the real TestInstances (carrying IDLab)
	 * server-side without touching the doc, so the FormData keeps the user's original GUID-only triples. This walks
	 * TestInstanceSampleJoins(by IDSample) → TestInstances → Tests, assembles each triple FROM the DB record
	 * (TestInstance with its IDLab + IDTestInstance identity, the DB TISJ, the DB TestInstanceLabJoin via
	 * `_attachAdoptedLabJoins`; DocumentPolyJoin left null for
	 * _buildTestInstanceTriple to synthesize against the doc, since none is persisted), writes them at the instances
	 * address, then setDataByInput the hashes. _syncTestInstances reconciles by IDTest and REUSES the adopted triples,
	 * so per-test labs + identities reflect persisted state and SampleTestChange redraws the Testing section.
	 * Persisted state is authoritative on (re)open.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index (null for the non-tabular SampleTestIDs field).
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {Record<string, any>} pSampleRecord - The bound Sample record (must carry a persisted IDSample).
	 */
	_loadSampleTests(pView, pInput, pRowIndex, pTransactionGUID, pSampleRecord)
	{
		const tmpRefreshEvent = 'SampleTestAdopt';
		pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpRefreshEvent);
		const fComplete = () => pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpRefreshEvent);
		this.pict.HeadlightRestClient.getJSON(`TestInstanceSampleJoins/FilteredTo/FBV~IDSample~EQ~${pSampleRecord.IDSample}`, (pError, pJoins) =>
		{
			const tmpJoins = Array.isArray(pJoins) ? pJoins.filter((pJoin) => parseInt(pJoin.IDTestInstance, 10) > 0) : [];
			if (!tmpJoins.length)
			{
				fComplete();
				return;
			}
			const tmpIDTestInstances = [ ...new Set(tmpJoins.map((pJoin) => parseInt(pJoin.IDTestInstance, 10))) ];
			this.pict.HeadlightRestClient.getJSON(`TestInstances/FilteredTo/FBL~IDTestInstance~INN~${tmpIDTestInstances.join(',')}`, (pError2, pTestInstances) =>
			{
				const tmpTestInstances = Array.isArray(pTestInstances) ? pTestInstances.filter((pTestInstance) => parseInt(pTestInstance.IDTest, 10) > 0) : [];
				if (!tmpTestInstances.length)
				{
					fComplete();
					return;
				}
				const tmpIDTests = [ ...new Set(tmpTestInstances.map((pTestInstance) => parseInt(pTestInstance.IDTest, 10))) ];
				this.pict.HeadlightRestClient.getJSON(`Tests/FilteredTo/FBL~IDTest~INN~${tmpIDTests.join(',')}`, (pError3, pTests) =>
				{
					const tmpHashByTest = {};
					for (const tmpTest of Array.isArray(pTests) ? pTests : [])
					{
						if (tmpTest && tmpTest.Hash)
						{
							tmpHashByTest[tmpTest.IDTest] = tmpTest.Hash;
						}
					}
					const tmpJoinByTI = {};
					for (const tmpJoin of tmpJoins)
					{
						tmpJoinByTI[parseInt(tmpJoin.IDTestInstance, 10)] = tmpJoin;
					}
					const tmpAdopted = [];
					const tmpHashes = [];
					for (const tmpTestInstance of tmpTestInstances)
					{
						const tmpHash = tmpHashByTest[tmpTestInstance.IDTest];
						if (!tmpHash)
						{
							continue;
						}
						const tmpJoin = tmpJoinByTI[parseInt(tmpTestInstance.IDTestInstance, 10)] || {};
						tmpAdopted.push(
							{
								Name: tmpTestInstance.Name || `Sample Test for ${tmpHash}`,
								TestInstance: ScopedEntityData.adoptTestInstance(this.pict, tmpTestInstance),
								TestInstanceSampleJoin: { IDTestInstanceSampleJoin: tmpJoin.IDTestInstanceSampleJoin, GUIDTestInstanceSampleJoin: tmpJoin.GUIDTestInstanceSampleJoin, IDSample: tmpJoin.IDSample, IDTestInstance: tmpTestInstance.IDTestInstance },
								DocumentPolyJoin: null,
							});
						tmpHashes.push(tmpHash);
					}
					if (!tmpAdopted.length)
					{
						fComplete();
						return;
					}
					this._attachAdoptedLabJoins(tmpAdopted, this._getTestInstances(pView, pInput, pRowIndex), () =>
					{
						const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
						const tmpInstancesAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instances`;
						this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpInstancesAddress, tmpAdopted);
						ScopedEntityData.beginProgrammaticLoad(this.pict);
						try
						{
							pView.setDataByInput(pInput, tmpHashes.join(','));
						}
						finally
						{
							ScopedEntityData.endProgrammaticLoad(this.pict);
						}
						fComplete();
					});
				});
			});
		});
	}

	/**
	 * The triple the form held before adoption for the same test: matched on the TestInstance id, then its GUID,
	 * then the Test.
	 *
	 * @param {Record<string, any>} pTriple - An adopted triple.
	 * @param {Array<Record<string, any>>} pHeldInstances - The triples the form held before adoption.
	 * @return {Record<string, any>|null} - The held triple, or null.
	 */
	_findHeldTriple(pTriple, pHeldInstances)
	{
		const tmpTestInstance = (pTriple && pTriple.TestInstance) || { };
		const tmpHeld = (Array.isArray(pHeldInstances) ? pHeldInstances : [ ]).filter((pHeldTriple) => pHeldTriple && pHeldTriple.TestInstance && typeof pHeldTriple.TestInstance === 'object');
		const tmpMatchers =
		[
			(pHeldTriple) => Number(tmpTestInstance.IDTestInstance) > 0 && Number(pHeldTriple.TestInstance.IDTestInstance) === Number(tmpTestInstance.IDTestInstance),
			(pHeldTriple) => !!tmpTestInstance.GUIDTestInstance && pHeldTriple.TestInstance.GUIDTestInstance === tmpTestInstance.GUIDTestInstance,
			(pHeldTriple) => Number(tmpTestInstance.IDTest) > 0 && Number(pHeldTriple.TestInstance.IDTest) === Number(tmpTestInstance.IDTest),
		];
		for (const fMatches of tmpMatchers)
		{
			const tmpMatch = tmpHeld.find(fMatches);
			if (tmpMatch)
			{
				return tmpMatch;
			}
		}
		return null;
	}

	/**
	 * Give each adopted triple the lab join the database holds for its TestInstance, so the per-test lab selector
	 * reuses the persisted row rather than minting a new one the (TestInstance, Lab) unique key refuses. Where a
	 * TestInstance has several, the one on its own lab wins. With no live row the triple keeps the lab join the form
	 * held for that test, and a failed read keeps every held one.
	 *
	 * @param {Array<Record<string, any>>} pAdopted - The triples assembled from the database.
	 * @param {Array<Record<string, any>>} pHeldInstances - The triples the form held before adoption.
	 * @param {() => void} fDone - Called once, when every triple's lab join is settled.
	 */
	_attachAdoptedLabJoins(pAdopted, pHeldInstances, fDone)
	{
		const fCarryHeld = (pTriple) =>
		{
			const tmpHeld = this._findHeldTriple(pTriple, pHeldInstances);
			if (tmpHeld && tmpHeld._TestInstanceLabJoin && typeof tmpHeld._TestInstanceLabJoin === 'object')
			{
				pTriple._TestInstanceLabJoin = tmpHeld._TestInstanceLabJoin;
			}
		};
		let tmpSettled = false;
		const fSettle = () =>
		{
			if (tmpSettled)
			{
				return;
			}
			tmpSettled = true;
			fDone();
		};
		const tmpIDTestInstances = [ ...new Set(pAdopted.map((pTriple) => parseInt(pTriple.TestInstance.IDTestInstance, 10)).filter((pID) => pID > 0)) ];
		if (tmpIDTestInstances.length < 1)
		{
			pAdopted.forEach(fCarryHeld);
			return fSettle();
		}
		try
		{
			this.pict.HeadlightRestClient.getJSON(`TestInstanceLabJoins/FilteredTo/FBL~IDTestInstance~INN~${tmpIDTestInstances.join(',')}`, (pError, pLabJoins) =>
			{
				try
				{
					if (pError || !Array.isArray(pLabJoins))
					{
						pAdopted.forEach(fCarryHeld);
						return;
					}
					const tmpRowsByTestInstance = { };
					for (const tmpRow of pLabJoins)
					{
						if (!tmpRow || !(Number(tmpRow.IDTestInstanceLabJoin) > 0) || typeof tmpRow.GUIDTestInstanceLabJoin !== 'string')
						{
							continue;
						}
						const tmpKey = parseInt(tmpRow.IDTestInstance, 10);
						tmpRowsByTestInstance[tmpKey] = tmpRowsByTestInstance[tmpKey] || [ ];
						tmpRowsByTestInstance[tmpKey].push(tmpRow);
					}
					for (const tmpTriple of pAdopted)
					{
						const tmpRows = tmpRowsByTestInstance[parseInt(tmpTriple.TestInstance.IDTestInstance, 10)] || [ ];
						if (tmpRows.length < 1)
						{
							fCarryHeld(tmpTriple);
							continue;
						}
						const tmpRow = tmpRows.find((pRow) => Number(pRow.IDLab) === Number(tmpTriple.TestInstance.IDLab)) || tmpRows[0];
						if (tmpRows.length > 1 && this.log && typeof this.log.warn === 'function')
						{
							this.log.warn(`MaterialsTestSelector: TestInstance ${tmpTriple.TestInstance.IDTestInstance} has ${tmpRows.length} lab joins; adopting ${tmpRow.IDTestInstanceLabJoin} (lab ${tmpRow.IDLab}).`);
						}
						tmpTriple._TestInstanceLabJoin =
						{
							IDTestInstanceLabJoin: Number(tmpRow.IDTestInstanceLabJoin),
							GUIDTestInstanceLabJoin: tmpRow.GUIDTestInstanceLabJoin,
							IDTestInstance: Number(tmpRow.IDTestInstance),
							IDLab: Number(tmpRow.IDLab),
						};
					}
				}
				catch (pError2)
				{
					pAdopted.forEach((pTriple) =>
					{
						if (!pTriple._TestInstanceLabJoin)
						{
							fCarryHeld(pTriple);
						}
					});
				}
				finally
				{
					fSettle();
				}
			});
		}
		catch (pError)
		{
			pAdopted.forEach(fCarryHeld);
			fSettle();
		}
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pRecord - The record that was refreshed.
	 * @param {string} pRecordAddress - The address where the record was stored.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		if (!this._isMultiple(pInput))
		{
			this._onRecordRefreshSingle(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
			return;
		}
		const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpTestInstancesAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instances`;
		if (!pRecord || (Array.isArray(pRecord) && pRecord.length === 0))
		{
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstancesAddress, [ ]);
			libLabRouting.requestSampleLabSync(this.pict);
			pView.marshalToView();
			super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
			return;
		}
		let tmpTestInstances = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstancesAddress);
		if (!Array.isArray(tmpTestInstances))
		{
			if (tmpTestInstances)
			{
				this.log.error(`MaterialsTestSelectorInputType: TestInstances at address ${tmpTestInstancesAddress} is not an array`, { tmpTestInstances });
			}
			tmpTestInstances = [ ];
		}
		const tmpSample = this._getBoundSampleRecord(pView, pInput);
		tmpTestInstances = this._syncTestInstances(Array.isArray(pRecord) ? pRecord : [ pRecord ], tmpTestInstances, tmpSample, pView, pInput, pRowIndex);
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstancesAddress, tmpTestInstances);
		libLabRouting.requestSampleLabSync(this.pict);
		//TOOD: is this a good idea? this ensures any fields that use this record are updated immediately, if they are in the same section
		pView.marshalToView();
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
	}

	/**
	 * Single-select (non-Multiple) record refresh. Writes the legacy singular TestInstance / DocumentPolyJoin /
	 * TestInstanceSampleJoin objects (one each) at the ${addr}_Instance / ${addr}_DocumentPolyJoin /
	 * ${addr}_SampleJoin addresses with un-suffixed GUIDs, matching the historical single-record data shape.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pRecord - The record that was refreshed.
	 * @param {string} pRecordAddress - The address where the record was stored.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 */
	_onRecordRefreshSingle(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpTestInstanceAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instance`;
		const tmpTestDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpTestAddress}_DocumentPolyJoin`;
		const tmpTestInstanceSampleJoinAddress = pInput.PictForm.TestInstanceSampleJoinAddress || `${tmpTestAddress}_SampleJoin`;
		if (!pRecord || (Array.isArray(pRecord) && pRecord.length === 0))
		{
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress, { });
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestDocumentPolyJoinAddress, { });
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceSampleJoinAddress, { });
			return;
		}
		const tmpTestHash = pRecord.Hash || '';
		const tmpSalt = this._getSalt(pView, pInput, pRowIndex);
		const tmpTest = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestAddress);
		//FIXME: do these need special tabular handling?
		let tmpTestInstance = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress);
		if (!tmpTestInstance || (typeof tmpTestInstance === 'object' && Object.keys(tmpTestInstance).length === 0))
		{
			tmpTestInstance = JSON.parse(JSON.stringify(pInput.PictForm.DefaultTestInstanceRecord ||
				{
					GUIDTestInstance: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TI-${tmpSalt}`,
					IDTest: pRecord.IDTest,
				}));
			if (tmpTestInstance && typeof tmpTestInstance === 'object')
			{
				this._translateRecordTemplateFields(tmpTestInstance);
			}
			if (!tmpTestInstance.GUIDTestInstance)
			{
				tmpTestInstance.GUIDTestInstance = `${this.pict.parseTemplate('{~D:AppData.DocumentData.ReportData.GUIDDocument~}', this.pict)}-TI-${tmpSalt}`;
			}
		}
		else
		{
			// Ensure GUID is set - use existing or generate new
			if (!tmpTestInstance.GUIDTestInstance)
			{
				tmpTestInstance.GUIDTestInstance = `${this.pict.parseTemplate('{~D:AppData.DocumentData.ReportData.GUIDDocument~}', this.pict)}-TI-${tmpSalt}`;
			}

			tmpTestInstance.IDTest = pRecord.IDTest;

			this._translateRecordTemplateFields(tmpTestInstance);
		}
		if (!tmpTestInstance.Name)
		{
			tmpTestInstance.Name = `Sample Test for ${tmpTestHash}`;
		}
		//NOTE: need to set this here in case it is used in the TestInstanceSampleJoin default record template
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress, tmpTestInstance);

		let tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestDocumentPolyJoinAddress);
		// A held join with no RemoteType is a stub left by a clear; rebuild it rather than stamp it.
		if (!tmpDocumentPolyJoin || typeof tmpDocumentPolyJoin !== 'object' || !tmpDocumentPolyJoin.RemoteType)
		{
			tmpDocumentPolyJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultDocumentPolyJoinRecord ||
				{
					GUIDDocumentPolyJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TI_DPJ-${this._getSalt(pView, pInput, pRowIndex)}`,
					RemoteType: 'TestInstance',
					IDDocument: `{~D:AppData.DocumentData.ReportData.IDDocument~}`,
					GUIDDocument: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}`,
					GUIDTestInstance: tmpTestInstance.GUIDTestInstance,
					_Dest_GUIDTestInstance: 'IDRemote',
				}));
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object')
			{
				this._translateRecordTemplateFields(tmpDocumentPolyJoin);
			}
		}
		const tmpDocument = this.pict.AppData.DocumentData.ReportData;
		if (tmpDocument)
		{
			if (tmpDocument.IDDocument > 0)
			{
				tmpDocumentPolyJoin.IDDocument = tmpDocument.IDDocument;
				delete tmpDocumentPolyJoin.GUIDDocument;
			}
			else
			{
				tmpDocumentPolyJoin.GUIDDocument = tmpDocument.GUIDDocument;
				delete tmpDocumentPolyJoin.IDDocument;
			}
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestDocumentPolyJoinAddress, tmpDocumentPolyJoin);
		let tmpTestInstanceSampleJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceSampleJoinAddress);
		const tmpSample = this._getBoundSampleRecord(pView, pInput);
		if (!tmpTestInstanceSampleJoin || (typeof tmpTestInstanceSampleJoin === 'object' && Object.keys(tmpTestInstanceSampleJoin).length === 0))
		{
			tmpTestInstanceSampleJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultTestInstanceSampleJoinRecord ||
				{
					GUIDTestInstanceSampleJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-TISJ-${this._getSalt(pView, pInput, pRowIndex)}`,
				}));
			if (tmpTestInstance && tmpTestInstance.GUIDTestInstance)
			{
				if (tmpTestInstance.IDTestInstance > 0)
				{
					tmpTestInstanceSampleJoin.IDTestInstance = tmpTestInstance.IDTestInstance;
					delete tmpTestInstanceSampleJoin.GUIDTestInstance;
				}
				else
				{
					tmpTestInstanceSampleJoin.GUIDTestInstance = tmpTestInstance.GUIDTestInstance;
				}
			}
			if (tmpSample && tmpSample.GUIDSample)
			{
				if (tmpSample.IDSample > 0)
				{
					tmpTestInstanceSampleJoin.IDSample = tmpSample.IDSample;
				}
				else
				{
					tmpTestInstanceSampleJoin.GUIDSample = tmpSample.GUIDSample;
				}
			}

			if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object')
			{
				this._translateRecordTemplateFields(tmpTestInstanceSampleJoin);
			}
			if (!tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin)
			{
				tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin = this.pict.getUUID();
			}
		}
		if (tmpTest && typeof tmpTest === 'object')
		{
			if (tmpTest.IDTest > 0)
			{
				tmpTestInstance.IDTest = tmpTest.IDTest;
				delete tmpTestInstance.GUIDTest;
			}
			else if (tmpTest.GUIDTest)
			{
				tmpTestInstance.GUIDTest = tmpTest.GUIDTest;
			}
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress, tmpTestInstance);
		//FIXME: should these use GUIDs to connect the records for offline? probably, but not sure how that'll work e2e
		if (tmpSample && typeof tmpSample === 'object')
		{
			if (tmpSample.IDSample > 0)
			{
				tmpTestInstanceSampleJoin.IDSample = tmpSample.IDSample;
				delete tmpTestInstanceSampleJoin.GUIDSample;
			}
			else if (tmpSample.GUIDSample)
			{
				tmpTestInstanceSampleJoin.GUIDSample = tmpSample.GUIDSample;
			}
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceSampleJoinAddress, tmpTestInstanceSampleJoin);
		//TOOD: is this a good idea? this ensures any fields that use this record are updated immediately, if they are in the same section
		pView.marshalToView();
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 *
	 * @return {Record<string, any>|null}
	 */
	_getBoundSampleRecord(pView, pInput)
	{
		const tmpPayloadUUID = this.pict.getUUID();
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`PushRecord:${this._getTrackingGUID(pInput)}:TempData["${tmpPayloadUUID}"]`);

		let tmpSample = this.pict.TempData[tmpPayloadUUID];
		delete this.pict.TempData[tmpPayloadUUID];

		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			//FIXME: what we actually need is a way to find this via the tracking GUID
			const tmpSampleAddress = pInput.PictForm.SampleAddress || 'SampleID__Record';
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		return tmpSample;
	}

	/**
	 * @param {(pError?: Error) => void} fCallback - The callback to call after the data pre-load.
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		if (!this._isScopedEntityDataEnabled())
		{
			this.pict.manifest.setValueAtAddress(this.pict, 'AppData.DocumentData.ReportData.FormData._EntityData', { });
		}
		const tmpEvent = 'ProjectEntityDataRecords:AppData.DocumentData.ReportData.FormData._EntityData';
		const tmpTransactionGUID = this.pict.getUUID();
		this.pict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(tmpEvent, tmpTransactionGUID);
		this.pict.views.PictFormMetacontroller.registerOnTransactionCompleteCallback(tmpTransactionGUID, () =>
		{
			this._sanitizeProjectedComprehensions();
			super.onBeforeSaveDataAsync(fCallback);
		});
		this.pict.views.PictFormMetacontroller.finalizeTransaction(tmpTransactionGUID);
	}

	/**
	 * Project a TestInstance into the save-time comprehension with its TestResult held back. The result
	 * is staged when the user sets it (`_stageDeferredTestResult`), not from here. Both Test families
	 * route through the same primitive.
	 *
	 * @param {Record<string, any>} pTestInstance - The TestInstance record being projected.
	 * @param {Record<string, any>} pEntityData - The save-time `<Entity>.<GUID>` target.
	 */
	_projectTestInstanceDeferringResult(pTestInstance, pEntityData)
	{
		if (!ScopedEntityData.projectTestInstanceDeferringResult(pTestInstance, pEntityData))
		{
			this.pict.log.warn(`MaterialsTestSelector: could not project the test instance ${pTestInstance?.GUIDTestInstance}; it will not be saved.`);
		}
	}

	/**
	 * Stage the test result the user just entered for the workflow trigger that applies it
	 * (`PictForm.DeferredComprehensionContext`, default `OnEnteringState.Approved`).
	 *
	 * Staging happens at the edit rather than on the save sweep: the sweep writes what the form holds,
	 * which cannot tell a result the user cleared from one that was never entered, and would re-assert a
	 * standing leaf on every save. A cleared result stages as a clear — no result is a valid outcome.
	 *
	 * @param {Record<string, any>} pTestInstance - The TestInstance whose result changed.
	 * @param {Object} pInput - The form input descriptor.
	 * @return {boolean} - True when the result was staged.
	 */
	_stageDeferredTestResult(pTestInstance, pInput)
	{
		const tmpGUID = pTestInstance && pTestInstance.GUIDTestInstance;
		if (!tmpGUID)
		{
			this.pict.log.warn('MaterialsTestSelector: a test result changed on an instance with no GUIDTestInstance; it cannot be staged for approval.');
			return false;
		}
		const tmpDeferredContext = ScopedEntityData.resolveDeferredContext(pInput, this.options?.DeferredComprehensionContext);
		return ScopedEntityData.stageDeferredValue(this.pict, tmpDeferredContext, 'TestInstance', tmpGUID, 'TestResult', pTestInstance.TestResult);
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {number|string|null} pRowIndex - The row index of the tabular data.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {Object} pTargetObject - The `<Entity>.<GUID>` object to project into.
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpTestAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}

		if (!this._isMultiple(pInput))
		{
			const tmpTestInstanceAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instance`;
			const tmpTestInstance = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceAddress);
			if (tmpTestInstance && typeof tmpTestInstance === 'object' && !Array.isArray(tmpTestInstance) && tmpTestInstance.GUIDTestInstance)
			{
				this._projectTestInstanceDeferringResult(tmpTestInstance, tmpEntityData);
			}

			const tmpTestInstanceSampleJoinAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_SampleJoin`;
			const tmpTestInstanceSampleJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstanceSampleJoinAddress);
			if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object' && !Array.isArray(tmpTestInstanceSampleJoin) && tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin && ScopedEntityData.referencesSample(tmpTestInstanceSampleJoin))
			{
				if (!tmpEntityData.TestInstanceSampleJoin || typeof tmpEntityData.TestInstanceSampleJoin !== 'object' || Array.isArray(tmpEntityData.TestInstanceSampleJoin))
				{
					tmpEntityData.TestInstanceSampleJoin = { };
				}
				tmpEntityData.TestInstanceSampleJoin[tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin] = tmpTestInstanceSampleJoin;
			}
			return;
		}

		const tmpTestInstancesAddress = pInput.PictForm.TestInstanceAddress || `${tmpTestAddress}_Instances`;
		const tmpTestInstances = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTestInstancesAddress);
		if (!Array.isArray(tmpTestInstances))
		{
			return;
		}
		for (const tmpTriple of tmpTestInstances)
		{
			if (!tmpTriple || typeof tmpTriple !== 'object')
			{
				continue;
			}
			const tmpTestInstance = tmpTriple.TestInstance;
			if (tmpTestInstance && typeof tmpTestInstance === 'object' && !Array.isArray(tmpTestInstance) && tmpTestInstance.GUIDTestInstance)
			{
				this._projectTestInstanceDeferringResult(tmpTestInstance, tmpEntityData);
			}

			const tmpTestInstanceSampleJoin = tmpTriple.TestInstanceSampleJoin;
			if (tmpTestInstanceSampleJoin && typeof tmpTestInstanceSampleJoin === 'object' && !Array.isArray(tmpTestInstanceSampleJoin) && tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin && ScopedEntityData.referencesSample(tmpTestInstanceSampleJoin))
			{
				if (!tmpEntityData.TestInstanceSampleJoin || typeof tmpEntityData.TestInstanceSampleJoin !== 'object' || Array.isArray(tmpEntityData.TestInstanceSampleJoin))
				{
					tmpEntityData.TestInstanceSampleJoin = { };
				}
				tmpEntityData.TestInstanceSampleJoin[tmpTestInstanceSampleJoin.GUIDTestInstanceSampleJoin] = tmpTestInstanceSampleJoin;
			}

			const tmpDocumentPolyJoin = tmpTriple.DocumentPolyJoin;
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				if (!tmpEntityData.DocumentPolyJoin || typeof tmpEntityData.DocumentPolyJoin !== 'object' || Array.isArray(tmpEntityData.DocumentPolyJoin))
				{
					tmpEntityData.DocumentPolyJoin = { };
				}
				tmpEntityData.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}
		}
	}

	/**
	 * @param {Record<string, any>} pRecord
	 * @param {Object} pInput
	 *
	 * @return {string}
	 */
	_getRecordDisplayName(pRecord, pInput)
	{
		if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
		{
			return '#INVALID#';
		}
		if (!pRecord.Name)
		{
			if (pRecord.Description)
			{
				return pRecord.Description;
			}
			if (pRecord.Hash)
			{
				return `Test Code ${pRecord.Hash}`;
			}
			return `Unnamed Test #${pRecord.IDTest ?? '??'}`;
		}
		return pRecord.Name;
	}

	/**
	 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex)
	{
		super._updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex);
		pConfigOptions.SearchFields = pConfigOptions.SearchFields || [ 'Hash', 'Name' ];
		//TODO: wrap existing HandleSearchFilters if it exists?
		pConfigOptions.HandleSearchFilters = (pFilters) =>
		{
			if (pFilters.length > 0)
			{
				pFilters.unshift('FOP~0~(~0');
				pFilters.push('FCP~0~)~0');
			}
			pFilters.push('FBV~Active~EQ~1');
			pFilters.push('FBV~Collection~EQ~0');
			return pFilters;
		};
	}
}

module.exports = MaterialsTestSelectorInputType;
module.exports.default_configuration = { AutoSaveDataWithApp: true };
